import React, { createContext, useContext, useState, useEffect } from 'react';
import { loginUser, registerUser } from '../services/api';
import { useOrderHistory } from './OrderHistoryContext';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);
  const { clearOrderHistory, setOrderHistory } = useOrderHistory();

  useEffect(() => {
    const storedUser = localStorage.getItem('tiwatinwa_user');
    if (storedUser) setUser(JSON.parse(storedUser));
    setIsLoading(false);
  }, []);

  const register = async (userData) => {
    setIsLoading(true);
    try {
      const data = await registerUser(userData);
      // Use backend response if available
      if (data.data && data.data.user) {
        localStorage.setItem('tiwatinwa_user', JSON.stringify(data.data.user));
        setUser(data.data.user);
        localStorage.setItem('token', data.data.token);
        setToken(data.data.token);
        setIsLoading(false);
        return { success: true, message: 'Registration successful!' };
      } else {
        setIsLoading(false);
        return { success: false, message: data.message || 'Registration failed. Please try again.' };
      }
    } catch (error) {
      setIsLoading(false);
      return { success: false, message: 'Registration failed. Please try again.' };
    }
  };

  const login = async (email, password) => {
    setIsLoading(true);
    try {
      const data = await loginUser(email, password);
      // Save token and user
      localStorage.setItem('token', data.data.token);
      if (data.data.user) {
        localStorage.setItem('tiwatinwa_user', JSON.stringify(data.data.user));
        setUser(data.data.user);
      }
      setIsLoading(false);
      return { success: true, message: 'Login successful!' };
    } catch (error) {
      setIsLoading(false);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const logout = () => {
    localStorage.removeItem('tiwatinwa_user');
    localStorage.removeItem('token'); // Remove token
    setToken(null); // (if you use a token state)
    setUser(null);
    clearOrderHistory(); // <-- add this
  };

  const updateProfile = (updates) => {
    const updatedUser = { ...user, ...updates };
    localStorage.setItem('tiwatinwa_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const addFavorite = (dish) => {
    if (!user) return;
    const exists = user.favoriteItems?.some(item => item.id === dish.id);
    if (exists) return;
    const updatedUser = {
      ...user,
      favoriteItems: [...(user.favoriteItems || []), dish]
    };
    localStorage.setItem('tiwatinwa_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const removeFavorite = (dishId) => {
    if (!user) return;
    const updatedUser = {
      ...user,
      favoriteItems: (user.favoriteItems || []).filter(item => item.id !== dishId)
    };
    localStorage.setItem('tiwatinwa_user', JSON.stringify(updatedUser));
    setUser(updatedUser);
  };

  const addOrderToHistory = (order, userId) => {
    setOrderHistory(prev => {
      const updated = [order, ...prev];
      localStorage.setItem(`orderHistory_${userId}`, JSON.stringify(updated));
      return updated;
    });
  };

  const saveToken = (data) => {
    localStorage.setItem('token', data.token); // Save token
    setToken(data.token); // (if you use a token state)
  };

  return (
    <AuthContext.Provider value={{ token, user, isLoading, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};